import { useEffect, useMemo, useState } from "react";
import Aside from "../../components/aside/aside.component";
import Footer from "../../components/footer/footer.component";
import Header from "../../components/header/header.component";
import { Link } from "react-router-dom";
import AlertPeriod from "../../components/alert-period/alert-period";
import { RegistersService } from "../../services/operation/registers";
import { UtilsService } from "../../services/utils";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import DatepickerComponent from "../../components/datepicker/datepicker.component";
import InvalidFeedback from "../../components/invalid-feedback/invalid-feedback.component";
import Button from "../../components/button/button.component";
import SelectComponent from "../../components/select/select.component";
import { PackingsService } from "../../services/packings";
import GeneralTable from "./general-table";
import EmptyState from "../../components/empty-state/empty-state.component";
import { ExcelService } from "../../utils/excel";
import { StorageService } from "../../services/storage";
import { STORAGE } from "../../constants";

declare const KTComponents: any;

function RegistersPage() {
  const filterStatuses = [
    { value: "-1", text: "Todos los estatus" },
    { value: "empaque", text: "Proceso de empaque" },
    { value: "pagos", text: "Proceso de pagos" },
  ];
  const filterPayments = [
    { value: "-1", text: "Todos los estatus" },
    { value: "liquidado", text: "Liquidado" },
    { value: "adeudado", text: "En adeudo" },
  ];
  const defaultValues = {
    range: "",
    status: "-1",
    payments: "-1",
    packing: "-1",
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<typeof defaultValues>({
    mode: "onChange",
    defaultValues,
  });

  const [packingList, setPackingList] = useState<any[]>([]);
  const [list, setList] = useState<any[]>([]);
  const [originalList, setOriginalList] = useState<any[]>([]);

  const totalAdeudado = useMemo(
    () => list.reduce((acc: number, row: any) => acc + row.valorTotal, 0),
    [list]
  );
  const totalPayments = useMemo(
    () => list.reduce((acc: number, row: any) => acc + row.totalPayments, 0),
    [list]
  );
  const totalDue = useMemo(
    () => list.reduce((acc: number, row: any) => acc + row.totalDue, 0),
    [list]
  );

  const getStatus = (row: any) => {
    if (row.packing) {
      return "Pagos";
    }

    const valorTotal = row.customers.reduce(
      (acc: number, c: any) => acc + c.valor_total,
      0
    );
    if (!valorTotal) {
      return "Empaque";
    }

    return "General";
  };

  const getList = async () => {
    let data = await RegistersService.list();

    data = data.map((row: any) => {
      const total = row.customers.reduce(
        (acc: number, c: any) => acc + c.valor_total,
        0
      );
      const totalPayments = row.customers.reduce(
        (acc: number, c: any) =>
          acc + c.pagos.reduce((payment: any, p: any) => payment + p.monto, 0),
        0
      );
      return {
        ...row,
        valorTotal: total,
        totalPayments: totalPayments,
        totalDue: total - totalPayments,
        status: getStatus(row).toLowerCase(),
      };
    });
    setList(data);
    setOriginalList(data);

  };

  const getPackingList = async () => {
    const data = await PackingsService.list();
    const all = { value: "-1", text: "Todos los empaques" };
    const f = data.map((row: any) => ({ value: row._id, text: row.name }));
    setPackingList([all, ...f]);
  };

  const filter = (data: typeof defaultValues) => {
    const range = data.range;
    const payments = data.payments;
    const status = data.status;
    const packing = data.packing;
    
    StorageService.save(STORAGE.FILTER_REGISTERS, {
      range,
      payments,
      status,
      packing
    })

    if (!range && status === "-1" && payments === "-1" && packing === "-1") {
      return setList(originalList);
    }

    const listFiltered = originalList
      .filter((row) => {
        return packing === "-1" || row.general.packing._id === packing;
      })
      .filter((row) => {
        return (
          payments === "-1" ||
          (row.valorTotal > 0 &&
            payments === "liquidado" &&
            row.totalDue <= 0) ||
          (payments === "adeudado" && row.totalDue > 0)
        );
      })
      .filter((row) => {
        return (
          status === "-1" ||
          (status === "empaque" && row.status === "empaque") ||
          (status === "pagos" && row.status === "pagos")
        );
      })
      .filter((row) => {
        return (
          range.length !== 2 ||
          moment(row.general.date).isBetween(
            moment(range[0]),
            moment(range[1]),
            "days",
            "[]"
          )
        );
      });

    setList(listFiltered);
  };

  const download = () => {
    const jsonToExport = list.map((row: any) => {
      return {
        "Fecha de creación": UtilsService.dateFormat(row.created_at),
        "Fecha": UtilsService.dateFormat(row.general.date, "DD/MM/YYYY"),
        "Procedencia": row.general.procedencia,
        "Productores": row.customers.map((customer: any) => `${customer.customer.name}: ${customer.field.name}`).join('\n'),
        "Folio de pesada": row.general.folio_pesada,
        "Empaque": row.general.packing.name,
        "No. de cajas": row.general.total_cajas,
        "Peso neto": row.general.peso_neto,
        "Peso por caja": row.general.peso_caja,
        "Valor total": UtilsService.numberFormat(row.valorTotal),
        "Total pagado": UtilsService.numberFormat(row.totalPayments),
        "Total adeudado": UtilsService.numberFormat(row.totalDue),
        "Estatus": getStatus(row),
      }
    })
    
    ExcelService.fromJson(jsonToExport);
  }

  useEffect(() => {
    getPackingList();
    getList();
  }, []);

  useEffect(() => {
    const filters = StorageService.get(STORAGE.FILTER_REGISTERS);
    if(filters) {
      reset(filters);
      filter(filters);
    }
    
  }, [packingList, originalList]);

  useEffect(() => {
    KTComponents.init();
  }, [list]);

  return (
    <>
      <div className="d-flex flex-column flex-root">
        <div className="page d-flex flex-row flex-column-fluid">
          <Aside />
          <div
            className="wrapper d-flex flex-column flex-row-fluid"
            id="kt_wrapper"
          >
            <Header />
            <div
              className="content fs-6 d-flex flex-column flex-column-fluid"
              id="kt_content"
            >
              <div className="toolbar" id="kt_toolbar">
                <div className="container-fluid d-flex flex-stack flex-wrap flex-sm-nowrap">
                  <div className="d-flex flex-column align-items-start justify-content-center flex-wrap me-2">
                    <h1 className="text-gray-900 fw-bold my-1 fs-2">
                      Cortes
                      <small className="text-muted fs-6 fw-normal ms-1" />
                    </h1>
                    <ul className="breadcrumb fw-semibold fs-base my-1">
                      <li className="breadcrumb-item text-muted">Operación</li>
                      <li className="breadcrumb-item text-gray-900">Cortes</li>
                    </ul>
                  </div>
                  <div className="d-flex align-items-center flex-nowrap text-nowrap py-1">
                    <Link className="btn btn-primary" to={"form"}>
                      <i className="ki-duotone ki-plus"></i> Nuevo registro
                    </Link>
                  </div>
                </div>
              </div>

              <div className="container-xxl mb-5">
                <AlertPeriod />
              </div>

              <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
                <div className="container-xxl">
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="d-flex align-items-end column-gap-4 mb-6">
                        <h4 className="mb-6">
                          Filtros <i className="bi bi-funnel-fill" />
                        </h4>
                        <div className="ms-auto">
                          <Button
                            className="btn btn-primary"
                            onClick={handleSubmit(filter)}
                          >
                            Filtrar
                          </Button>
                        </div>
                        <div className="nowrap">
                          <Button className="btn btn-light-primary" onClick={download}>
                            <i className="bi bi-download"></i>
                            Descargar
                          </Button>
                        </div>
                      </div>

                      <div className="d-flex align-items-end column-gap-4">
                        <div className="flex-grow-1">
                          <Controller
                            name="range"
                            control={control}
                            rules={{ required: false }}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => {
                              let valueFormatted = "";

                              if (Array.isArray(value) && value.length === 2) {
                                const start = moment(value[0]).format(
                                  "DD-MM-YYYY"
                                );
                                const end = value[1]
                                  ? moment(value[1]).format("DD-MM-YYYY")
                                  : "";
                                valueFormatted = `Del ${start} al ${end}`;
                              }
                              return (
                                <>
                                  <DatepickerComponent
                                    label="Rango de fechas"
                                    errors={error}
                                    onChange={onChange}
                                    value={valueFormatted}
                                    mode="range"
                                    disable={[
                                      {
                                        from: moment()
                                          .add(1, "days")
                                          .format("YYYY-MM-DD"),
                                        to: moment()
                                          .add(1, "years")
                                          .format("YYYY-MM-DD"),
                                      },
                                    ]}
                                  />
                                  <InvalidFeedback error={error} />
                                </>
                              );
                            }}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <SelectComponent
                            label="Empaque"
                            data={packingList}
                            {...register("packing", { required: false })}
                            errors={errors.packing}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <SelectComponent
                            label="Estatus de registro"
                            data={filterStatuses}
                            {...register("status", { required: false })}
                            errors={errors.status}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <SelectComponent
                            label="Estatus de pagos"
                            data={filterPayments}
                            {...register("payments", { required: false })}
                            errors={errors.payments}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 d-flex column-gap-4">
                    <div className="card flex-grow-1 text-center">
                      <div className="card-body">
                        <div className="fs-2x fw-bold">
                          $ {UtilsService.numberFormat(totalAdeudado)}
                        </div>
                        <div className="fs-4 fw-semibold text-gray-500">
                          Ventas totales
                        </div>
                      </div>
                    </div>
                    <div className="card flex-grow-1 text-center">
                      <div className="card-body">
                        <div className="fs-2x fw-bold">
                          $ {UtilsService.numberFormat(totalPayments)}
                        </div>
                        <div className="fs-4 fw-semibold text-gray-500">
                          Total pagado
                        </div>
                      </div>
                    </div>
                    <div className="card flex-grow-1 text-center">
                      <div className="card-body">
                        <div className="fs-2x fw-bold">
                          $ {UtilsService.numberFormat(totalDue)}
                        </div>
                        <div className="fs-4 fw-semibold text-gray-500">
                          Total adeudado
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-body py-4">
                      <div
                        className="dt-container dt-bootstrap5 dt-empty-footer"
                        id="kt_table_users_wrapper"
                      >
                        <div className="table-responsive" id="">
                          <GeneralTable list={list} />
                          {list.length === 0 && <EmptyState />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default RegistersPage;
